import { Injectable, OnDestroy, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import { Subject, fromEvent, merge, timer } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})

export class IdleTokenService implements OnDestroy {
  private msalService = inject(MsalService);
  private router = inject(Router);

  private readonly refreshBufferMs = 5 * 60 * 1000; 
  private readonly fallbackDelayMs = 55 * 60 * 1000;

  private destroy$ = new Subject<void>();
  private stop$ = new Subject<void>(); 
  
  // Emit warnings for UI notifications
  tokenWarning$ = new Subject<void>();
  tokenRefreshed$ = new Subject<void>();

  /**
   * Initialize idle/refresh monitoring - call this once in app.component ngOnInit
   * after successful authentication
   */
  startIdleMonitoring(): void {
    this.scheduleRefreshFromTokenExpiry();

    merge(
      fromEvent(document, 'mousedown'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'scroll'),
      fromEvent(document, 'touchstart'),
      fromEvent(document, 'click'),
    )
      .pipe(debounceTime(1000), takeUntil(this.stop$))
      .subscribe();
  }

  stopIdleMonitoring(): void {
    this.stop$.next();
  }

  refreshTokenNow(): void {
    this.refreshToken();
  }

  private scheduleRefreshFromTokenExpiry(): void {
    const account = this.msalService.instance.getActiveAccount();
    const claims = account?.idTokenClaims as any;
    const exp = claims?.exp as number | undefined;
    const iat = claims?.iat as number | undefined;

    let delay = this.fallbackDelayMs;

    if (exp) {
      const expiryMs = exp * 1000;
      const nowMs = Date.now();
      const msUntilExpiry = expiryMs - nowMs;
      delay = Math.max(msUntilExpiry - this.refreshBufferMs, 0);

      if (iat) {
        const tokenLifetimeSec = exp - iat;
      }
    } else {
      console.warn(
        '[IdleTokenService] No exp claim found on idTokenClaims — using fallback delay of',
        this.fallbackDelayMs / 1000 / 60,
        'min',
      );
    }

    timer(Math.max(delay - 60_000, 0))
      .pipe(takeUntil(this.stop$), takeUntil(this.destroy$))
      .subscribe(() => {
        this.tokenWarning$.next();
      });

    timer(delay)
      .pipe(takeUntil(this.stop$), takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshToken();
      });
  }

  private refreshToken(): void {
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount) {
      console.warn('[IdleTokenService] No active account found - redirecting to login');
      this.router.navigateByUrl('/login');
      return;
    }

    this.msalService
      .acquireTokenSilent({
        account: activeAccount,
        scopes: ['user.read'], // Adjust to your app's required scopes
        forceRefresh: true, // always hit Azure AD, bypass MSAL's cache
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tokenResponse) => {

          // Update localStorage with new token
          localStorage.setItem('tokenId', JSON.stringify(tokenResponse.idToken));

          // Emit event for components to react to refresh
          this.tokenRefreshed$.next();

          // Reschedule based on the NEW token's exp
          this.scheduleRefreshFromTokenExpiry();
        },
        error: (err) => {
          console.error('[IdleTokenService] Silent token refresh failed:', err);

          // Check if error is due to network or genuine token/session expiration
          if (err?.error_codes?.includes(65001) || err?.error_description?.includes('AADSTS65001')) {
            // Interaction required - force interactive login
            
            this.msalService.loginRedirect({ scopes: ['user.read'] });
          } else {
            // Other errors - redirect to unauthorized
            this.router.navigateByUrl('/unauthorized');
          }
        },
      });
  }

  // FIX: Complete all subjects in ngOnDestroy
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // FIX: Complete all subjects to release subscribers
    this.stop$.complete();
    this.tokenWarning$.complete();
    this.tokenRefreshed$.complete();
  }
}